<template>
  <!--
    Used to manage the sounds that are used in videoPostprocessing.
  -->
  <div class="soundsList">
    <LoadingPlaceholder v-if="loading" />
    
    <template v-else>
      <Grid
        :ref="kgm_ref"
        :style="{height: 'auto'}"
        :data-items="kgm_computedGridItems(sounds)"
        :columns="kgm_responsiveColumns()"
        :filterable="true"
        :filter="kgm_filter"
        :pageable="kgm_pagable"
        :page-size="kgm_take"
        :skip="kgm_skip"
        :take="kgm_take"
        :total="kgm_allGridItems(sounds)"
        :sortable="{
          allowUnsort: kgm_allowUnsort,
          mode: kgm_sortMode
        }"
        :sort="kgm_sort"
        selected-field="selected"
        @rowclick="kgm_gridOnRowClick"
        @filterchange="kgm_gridFilterChange"
        @pagechange="kgm_gridPageChange"
        @sortchange="kgm_gridSortChange"
      >
        <div
          slot="nameFilter"
          slot-scope="{props, methods}"
          class="input-group"
        >
          <input
            ref="nameFilterInput"
            :value="props.value"
            type="text"
            class="form-control mr-2 rounded-right"
            placeholder="please enter a name"
            @input="(event) => {methods.change({operator: 'contains', field: props.field, value: event.target.value, syntheticEvent: event});}"
          >
          <div
            v-if="props.value"
            class="input-group-append"
          >
            <button
              class="btn btn-light border rounded"
              @click="kgm_resetInput('nameFilterInput', (event) => {methods.change({operator: '', field: '', value: '', syntheticEvent: event});})"
            >
              <font-awesome-icon
                class="gray"
                icon="times"
              />
            </button>
          </div>
        </div>
        <div
          slot="descriptionFilter"
          slot-scope="{props, methods}"
          class="input-group"
        >
          <input
            ref="descriptionFilterInput"
            :value="props.value"
            type="text"
            class="form-control mr-2 rounded-right"
            placeholder="please enter a description"
            @input="(event) => {methods.change({operator: 'contains', field: props.field, value: event.target.value, syntheticEvent: event});}"
          >
          <div
            v-if="props.value"
            class="input-group-append"
          >
            <button
              class="btn btn-light border rounded"
              @click="kgm_resetInput('descriptionFilterInput', (event) => {methods.change({operator: '', field: '', value: '', syntheticEvent: event});})"
            >
              <font-awesome-icon
                class="gray"
                icon="times"
              />
            </button>
          </div>
        </div>
        <template
          slot="activeTemplate"
          slot-scope="{props}"
        >
          <td>
            <font-awesome-icon
              v-if="!props.dataItem.active"
              class="red"
              icon="times"
            />
            <font-awesome-icon
              v-else
              class="green"
              icon="check"
            />
          </td>
        </template>
        <template
          slot="optionsTemplate"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <div class="btn-group">
              <button
                class="btn btn-primary btn-sm"
                @click="showSidebarEdit(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="edit"
                />
                <span>Edit</span>
              </button>
              <button
                v-if="!props.dataItem.audio.playing"
                class="btn btn-primary btn-sm"
                @click="play(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="play"
                />
                <span>Play</span>
              </button>
              <button
                v-if="props.dataItem.audio.playing"
                class="btn btn-primary btn-sm"
                @click="pause(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="pause"
                />
                <span>Stop</span>
              </button>
              <button
                class="btn btn-primary btn-sm"
                @click="download(props.dataItem)"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="download"
                />
                <span>Download</span>
              </button>
              <button
                class="btn btn-secondary btn-sm"
                @click="openConfirmModal(props.dataItem.id)"
              >
                <font-awesome-icon
                  class="mr-2 gray"
                  icon="trash"
                />
                <span>Remove</span>
              </button>
            </div>
          </td>
        </template>
      </Grid>
      <Sidebar
        v-if="showSidebar"
        :show-sidebar="showSidebar"
        @close="close"
      >
        <SoundsAdd
          v-if="addingElement"
          @reload="reloadSounds"
        />
        <SoundsEdit
          v-else
          :sound="sound"
          @reload="reloadSounds"
        />
      </Sidebar>
    </template>

    <SweetModal
      ref="confirmDelete"
      :title="$t('introOutroListComp.deleteElement')"
      icon="warning"
      blocking
      class="overflowHidden"
    >
      <p>{{ $t('introOutroListComp.sureToDelete') }}</p>
      <button
        slot="button"
        class="btn btn-secondary float-left mb-3"
        @click="$refs.confirmDelete.close()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="times"
        />{{ $t('cancel') }}
      </button>
      <button
        slot="button"
        class="btn btn-danger float-right mb-3"
        @click="removeSound()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="trash"
        />{{ $t('delete') }}
      </button>
      <div class="clearfix" />
    </SweetModal>
  </div>
</template>

<script>

import { SweetModal } from 'sweet-modal-vue';
import { kendoGridMixin } from '@/mixins/kendoGridMixin.js';
import Sidebar from '@/components/Base/Sidebar.vue';
import SoundsEdit from '@/components/VideoPostprocessing/Sounds/SoundsEdit.vue';
import SoundsAdd from '@/components/VideoPostprocessing/Sounds/SoundsAdd.vue';
import { dateTimeMixin } from '@/mixins/dateTimeMixin.js';

export default {
  name: "SoundsList",
  components: {
    SweetModal,
    Sidebar,
    SoundsEdit,
    SoundsAdd
  },
  mixins: [
    kendoGridMixin,
    dateTimeMixin
  ],
  data () {
    return {
      loading: true,
      sounds: null,
      showSidebar: false,
      addingElement: false,
      currentId: null,
      deleteIndex: null,
      kgm_filter: {
        logic: "and",
        filters: [
          {
            field: 'name',
            operator: 'contains',
            value: ''
          }
        ]
      },
      kgm_sort: [
        {
          "field": "name",
          "dir": "asc"
        }
      ],
      kgm_columns: [
        {
          field: 'name',
          filterable: true,
          filter: 'text',
          title: 'Name',
          filterCell: "nameFilter"
        },
        {
          field: 'description',
          filterable: true,
          filter: 'text',
          title: 'Description',
          filterCell: "descriptionFilter"
        },
        {
          field: 'active',
          filterable: false,
          title: 'Active',
          cell: 'activeTemplate'
        },
        {
          filterable: false,
          title: 'Options',
          cell: 'optionsTemplate',
          width: '350px'
        }
      ]
    }
  },
  metaInfo () {
    return {
      title: this.$t('menu.sounds')
    }
  },
  created () {
    this.loadingSounds();
  },
  methods: {
    //#region Sidebar
    show () {
      this.showSidebar = true;
    },
    close () {
      this.showSidebar = false;
    },
    showSidebarAdd () {
      this.addingElement = true;
      this.show();
    },
    showSidebarEdit (emitValues) {
      this.setCurrentSound(emitValues);
      this.addingElement = false;
      this.show();
    },
    setCurrentSound (sound) {
      this.sound = Object.assign({}, sound); //clones Object without reference to fix close sidebar issue
    },
    //#endregion

    //#region API-calls
    loadingSounds () {
      this.loading = true;
      this.getSounds();
    },
    reloadSounds () {
      this.$emit("reloadAuditLogs");
      this.showSidebar = false;
      this.getSounds();
    },
    openConfirmModal (index) {
      this.deleteIndex = index; //sound does not have an Id at the moment
      this.$refs.confirmDelete.open();
    },
    removeSound () {
      this.axios.delete(`/VideoPostProcessing/DeleteSoundElement?soundElementId=${ this.deleteIndex }`)
        .then(() => {
          this.reloadSounds();
          this.$emit("reloadAuditLogs");
        })
        .finally(() => {
          this.deleteIndex = null;
          this.$refs.confirmDelete.close();
        });
    },
    getSounds () {
      this.axios.get('/VideoPostProcessing/GetAllSoundElements')
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          this.sounds = response.data;
          this.sounds.forEach(function (element) {
            element.audio = new Audio();    
            element.audio.playing = false;   
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    //#region Soundplayer
    play (sound) {
      this.axios.get(`/VideoPostProcessingResource/GetSound/${ sound.id }`)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          sound.audio.src = response.data.url; 
          sound.audio.playing = true;
          sound.audio.play();
          this.$forceUpdate(); 
          sound.audio.onended = () => {
            sound.audio.playing = false;
            this.$forceUpdate(); 
          };
        })
        .finally(() => {
          this.loading = false;
        });
    },
    pause (sound) {
      sound.audio.pause();
      sound.audio.playing = false;
      this.$forceUpdate(); 
    },
    //#endregion
    download (sound) {
      this.axios.get(`/VideoPostProcessingResource/GetSound/${ sound.id }`)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          var fileLink = document.createElement('a');
          fileLink.href = response.data.url;
          fileLink.setAttribute('download', sound.sourceName);
          document.body.appendChild(fileLink);
          fileLink.click();
        })
        .finally(() => {
          this.loading = false;
        });
    } 
    //#endregion
  }
}
</script>
<style>
.soundsList .k-grid table tr:hover td {
  background :rgb(219, 219, 219) !important;
  cursor: pointer !important;
}
</style>